import {Discount} from '@shared/discount';
import {toNumberOr} from './numbers';

/**
 *
 * Comparator for discount rules:
 *
 * Percent discount has priority over the other discounts
 *
 * 1. higher percentage discount
 * 2. bigger flat discount (more negative ⇒ better)
 * 3. lower special price
 * 4. higher min_amount first
 *
 * -> https://pharmakon-software.atlassian.net/browse/PDEV-430?focusedCommentId=35779
 */
export const compareDiscountRules = (a: Discount, b: Discount): number => {
    const aPercent = toNumberOr(a.discount, 0);
    const bPercent = toNumberOr(b.discount, 0);
    if (bPercent !== aPercent) {
        return bPercent - aPercent;
    }

    const aFlat = toNumberOr(a.flat_discount, 0);
    const bFlat = toNumberOr(b.flat_discount, 0);
    if (aFlat !== bFlat) {
        return aFlat - bFlat;
    }

    const aSpecial = toNumberOr(a.special_price, Infinity);
    const bSpecial = toNumberOr(b.special_price, Infinity);
    if (aSpecial !== bSpecial) {
        return aSpecial - bSpecial;
    }

    if (b.min_amount !== a.min_amount) {
        return b.min_amount - a.min_amount;
    }

    return 0;
};

/**
 * Pick the best applicable rule for a given quantity.
 */
export const findBestRuleForQuantity = (rules: Discount[], quantity: number): Discount | null => (
    rules
        .filter((r) => quantity >= (r.min_amount ?? 0))
        .slice()
        .sort(compareDiscountRules)[0] ?? null
);